import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Heading,
  Divider,
  Box,
  Spinner,
  Center,
  useToast,
} from '@chakra-ui/react';
import { useGetAppProfileQuery, useSaveAppProfileMutation } from '../../store/linksApi';
import type { AppProfile } from '../../types/link';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * App Profile — the tenant's app identity, set ONCE and applied to every app
 * link automatically (store fallbacks + social-browser breakout). AppsFlyer-style:
 * the marketer never re-enters package / scheme / store URLs per link.
 */
export const AppProfileModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const { data, isLoading, isFetching } = useGetAppProfileQuery(undefined, { skip: !isOpen });
  const [save, { isLoading: saving }] = useSaveAppProfileMutation();
  const [form, setForm] = useState<AppProfile>({});

  useEffect(() => {
    if (isOpen && data) setForm(data);
  }, [isOpen, data]);

  const set = (k: keyof AppProfile, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    try {
      await save(form).unwrap();
      toast({ title: 'App Profile saved', status: 'success', duration: 3000, isClosable: true });
      onClose();
    } catch (e) {
      toast({
        title: 'Could not save App Profile',
        description: (e as { message?: string })?.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottomWidth="1px">App Profile</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={4} p={3} bg="blue.50" rounded="md" fontSize="xs" color="blue.800">
            Set your app's identity <b>once</b>. It's applied to every app link automatically —
            store fallbacks plus social in-app-browser breakout (Instagram, TikTok, Facebook…) — so
            you never re-enter it per link.
          </Box>
          {isLoading || isFetching ? (
            <Center py={10}>
              <Spinner />
            </Center>
          ) : (
            <VStack spacing={4} align="stretch" py={1}>
              <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                App identity
              </Heading>
              <FormControl>
                <FormLabel>Android package name</FormLabel>
                <Input
                  placeholder="com.example.app"
                  value={form.androidPackageName ?? ''}
                  onChange={(e) => set('androidPackageName', e.target.value)}
                />
                <FormHelperText>Builds the Android intent that escapes social in-app browsers.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>App scheme (optional)</FormLabel>
                <Input
                  placeholder="exampleapp"
                  value={form.appScheme ?? ''}
                  onChange={(e) => set('appScheme', e.target.value)}
                />
                <FormHelperText>
                  Custom URL scheme (no ://) to open an installed app. Leave blank — Android breakout
                  still works via the package name, and iOS falls back to the App Store.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>iOS bundle ID (optional)</FormLabel>
                <Input
                  placeholder="com.example.app"
                  value={form.iosBundleId ?? ''}
                  onChange={(e) => set('iosBundleId', e.target.value)}
                />
              </FormControl>

              <Divider />
              <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Store URLs
              </Heading>
              <FormControl>
                <FormLabel>Android Play Store URL</FormLabel>
                <Input
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  value={form.androidAppStoreUrl ?? ''}
                  onChange={(e) => set('androidAppStoreUrl', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>iOS App Store URL</FormLabel>
                <Input
                  placeholder="https://apps.apple.com/app/id..."
                  value={form.iosAppStoreUrl ?? ''}
                  onChange={(e) => set('iosAppStoreUrl', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>iPad-only App Store URL (optional)</FormLabel>
                <Input
                  placeholder="https://apps.apple.com/app/id... (iPad build)"
                  value={form.ipadAppStoreUrl ?? ''}
                  onChange={(e) => set('ipadAppStoreUrl', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Huawei AppGallery URL (optional)</FormLabel>
                <Input
                  placeholder="https://appgallery.huawei.com/app/..."
                  value={form.huaweiAppGalleryUrl ?? ''}
                  onChange={(e) => set('huaweiAppGalleryUrl', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Web fallback URL</FormLabel>
                <Input
                  placeholder="https://example.com (desktop / other devices)"
                  value={form.webFallbackUrl ?? ''}
                  onChange={(e) => set('webFallbackUrl', e.target.value)}
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth="1px" gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={saving} loadingText="Saving">
            Save App Profile
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
